import { LAB_REPORT } from "@/api_framework/api_config";
import clsx from "clsx";
import { IoFlaskOutline } from "react-icons/io5";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const RECONNECT_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
const STREAM_IDLE_TIMEOUT_MS = 45_000;
const LRI_ALERTS_LOCK_KEY = "lri-alerts";

interface ILRIAlertEvent {
  type: "snapshot" | "report_uploaded" | "count_updated";
  requisition_id: string | null;
  pending_count: number;
  delayed_count: number;
  text?: string;
  ts: number;
}

const parseSSEFrame = (frame: string): ILRIAlertEvent | null => {
  const data = frame
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).replace(/^ /, ""))
    .join("\n");
  if (!data) return null;
  try {
    const parsed = JSON.parse(data) as ILRIAlertEvent;
    return parsed?.text ? parsed : null;
  } catch {
    return null;
  }
};

export const useLabReportInterpretationAlertStreamAPI = (enabled: boolean) => {
  const configuration = useConfiguration();
  const accessToken = useAppSelector((state) => state?.auth?.authorization?.accessToken);
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const lriUrl = getRouteKey("LAB_REPORT_INTERPRETATION", "url");
  const [retryNonce, setRetryNonce] = useState(0);
  const stoppedUnauthorizedRef = useRef(false);

  const showAlert = useCallback(
    (event: ILRIAlertEvent) => {
      toast.custom(
        (t) => (
          <div
            onClick={() => {
              toast.dismiss(t.id);
              navigate(lriUrl);
            }}
            className={clsx(
              "flex items-center gap-3 bg-white px-4 py-3 border border-[#E0E0E0] border-l-4 border-l-[#193A6A] rounded-xl shadow-[0_12px_32px_rgba(25,58,106,0.18)] cursor-pointer transition-all duration-200",
              t.visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2",
            )}
          >
            <div className="flex justify-center items-center bg-[#193A6A]/10 rounded-full w-9 h-9 text-[#193A6A] shrink-0">
              <IoFlaskOutline className="w-5 h-5" />
            </div>
            <p className="font-semibold text-[#263238] text-sm">{event.text}</p>
          </div>
        ),
        { id: "lri-alert", duration: 8000 },
      );
    },
    [navigate, lriUrl],
  );

  useEffect(() => {
    if (!enabled) return;

    const session = new AbortController();
    const channel =
      navigator.locks && typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(LRI_ALERTS_LOCK_KEY) : null;
    stoppedUnauthorizedRef.current = false;

    // Settles when `signal` aborts or the server rejects the stream (401/403), so a held lock is never left dead
    const runStream = (signal: AbortSignal, onAlert: (event: ILRIAlertEvent) => void) =>
      new Promise<void>((resolve) => {
        let attempt = 0;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        signal.addEventListener(
          "abort",
          () => {
            if (reconnectTimer) clearTimeout(reconnectTimer);
            resolve();
          },
          { once: true },
        );

        const connect = async () => {
          if (signal.aborted) return;
          const connection = new AbortController();
          const abortConnection = () => connection.abort();
          signal.addEventListener("abort", abortConnection);

          let idleTimer: ReturnType<typeof setTimeout> | null = null;
          const armIdleWatchdog = () => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(abortConnection, STREAM_IDLE_TIMEOUT_MS);
          };

          let shouldReconnect = true;
          try {
            armIdleWatchdog();
            const response = await fetch(LAB_REPORT.LAB_REPORT_INTERPRETATION_ALERTS_STREAM.baseURL, {
              headers: {
                Authorization: localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key) ?? "",
                Accept: "text/event-stream",
              },
              cache: "no-store",
              signal: connection.signal,
            });

            if (response.status === 401) {
              stoppedUnauthorizedRef.current = true;
              shouldReconnect = false;
            } else if (response.status === 403) {
              shouldReconnect = false;
            } else if (response.ok && response.body) {
              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              let buffer = "";

              for (;;) {
                const { value, done } = await reader.read();
                if (done) break;
                armIdleWatchdog();
                attempt = 0;
                buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
                const frames = buffer.split("\n\n");
                buffer = frames.pop() ?? "";
                frames.forEach((frame) => {
                  const event = parseSSEFrame(frame);
                  if (event) onAlert(event);
                });
              }
            }
          } catch {
            shouldReconnect = !signal.aborted;
          } finally {
            if (idleTimer) clearTimeout(idleTimer);
            signal.removeEventListener("abort", abortConnection);
          }

          if (signal.aborted) return;
          if (!shouldReconnect) return resolve();
          const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)];
          attempt += 1;
          reconnectTimer = setTimeout(connect, delay);
        };

        connect();
      });

    if (!channel) {
      runStream(session.signal, showAlert);
      return () => session.abort();
    }

    const relayAlert = (event: ILRIAlertEvent) => {
      showAlert(event);
      if (!session.signal.aborted) channel.postMessage(event);
    };

    // Newest tab steals the lock so a refresh always reconnects; the tab it took over from queues as standby
    const ownStream = (steal: boolean) => {
      const ownership = new AbortController();
      const endOwnership = () => ownership.abort();
      session.signal.addEventListener("abort", endOwnership, { once: true });
      navigator.locks
        .request(LRI_ALERTS_LOCK_KEY, steal ? { steal: true } : { signal: session.signal }, () =>
          runStream(ownership.signal, relayAlert),
        )
        .catch(() => {
          endOwnership();
          session.signal.removeEventListener("abort", endOwnership);
          if (!session.signal.aborted) ownStream(false);
        });
    };

    channel.onmessage = (messageEvent: MessageEvent<ILRIAlertEvent>) => {
      if (messageEvent.data && !session.signal.aborted) showAlert(messageEvent.data);
    };
    ownStream(true);

    return () => {
      session.abort();
      channel.onmessage = null;
      channel.close();
    };
  }, [enabled, retryNonce, showAlert, configuration]);

  useEffect(() => {
    if (stoppedUnauthorizedRef.current && accessToken) {
      stoppedUnauthorizedRef.current = false;
      setRetryNonce((prev) => prev + 1);
    }
  }, [accessToken]);
};
